import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, TextField, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid, MenuItem,
  InputAdornment, CircularProgress, LinearProgress, Tabs, Tab
} from '@mui/material';
import { Add, Search, Warning, Refresh } from '@mui/icons-material';
import api from '../../api/axios';
import toast from 'react-hot-toast';

const categories = ['medicine', 'equipment', 'consumable', 'surgical', 'diagnostic', 'other'];

const defaultForm = { name: '', category: 'medicine', quantity: 0, minimumStock: 10, costPrice: 0, sellingPrice: 0, unit: 'units', manufacturer: '', batchNumber: '', expiryDate: '' };

const Inventory = () => {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState(0);
  const [open, setOpen] = useState(false);
  const [restockItem, setRestockItem] = useState<any>(null);
  const [restockQty, setRestockQty] = useState(0);
  const [form, setForm] = useState<any>(defaultForm);
  const [total, setTotal] = useState(0);

  const load = async () => {
    try {
      const params: any = { search, limit: 100 };
      if (tab === 1) params.lowStock = 'true';
      if (tab > 1) params.category = categories[tab - 2];
      const res = await api.get('/inventory', { params });
      setItems(res.data.data);
      setTotal(res.data.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [search, tab]);

  const handleAdd = async () => {
    try {
      await api.post('/inventory', form);
      toast.success('Item added to inventory');
      setOpen(false);
      setForm(defaultForm);
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to add item');
    }
  };

  const handleRestock = async () => {
    try {
      await api.post(`/inventory/${restockItem._id}/restock`, { quantity: restockQty });
      toast.success('Stock updated');
      setRestockItem(null);
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Restock failed');
    }
  };

  const stockColor = (item: any) => {
    if (item.quantity === 0) return '#d32f2f';
    if (item.quantity <= item.minimumStock) return '#f57c00';
    return '#388e3c';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Inventory</Typography>
          <Typography variant="body2" color="text.secondary">{total} items tracked</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => setOpen(true)} sx={{ borderRadius: 2 }}>
          Add Item
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search items..." value={search} onChange={e => setSearch(e.target.value)}
          size="small" sx={{ maxWidth: 300 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
        />
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="All Items" />
        <Tab label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Warning fontSize="small" />Low Stock</Box>} />
        {categories.map(c => <Tab key={c} label={c.charAt(0).toUpperCase() + c.slice(1)} />)}
      </Tabs>

      <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f9fafb' }}>
                  {['Item Name', 'Category', 'Quantity', 'Stock Level', 'Cost Price', 'Selling Price', 'Expiry', 'Actions'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, py: 1.5 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map(item => (
                  <TableRow key={item._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{item.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{item.manufacturer}</Typography>
                    </TableCell>
                    <TableCell><Chip label={item.category} size="small" variant="outlined" /></TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700} color={stockColor(item)}>
                        {item.quantity} {item.unit}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min((item.quantity / item.maximumStock) * 100, 100)}
                        sx={{ height: 6, borderRadius: 3, bgcolor: '#f0f0f0', '& .MuiLinearProgress-bar': { bgcolor: stockColor(item) } }}
                      />
                      {item.quantity <= item.minimumStock && (
                        <Typography variant="caption" color="error">Low Stock</Typography>
                      )}
                    </TableCell>
                    <TableCell>₹{item.costPrice}</TableCell>
                    <TableCell>₹{item.sellingPrice}</TableCell>
                    <TableCell>
                      {item.expiryDate ? (
                        <Typography variant="caption" color={new Date(item.expiryDate) < new Date() ? 'error' : 'text.secondary'}>
                          {new Date(item.expiryDate).toLocaleDateString()}
                        </Typography>
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => { setRestockItem(item); setRestockQty(0); }} title="Restock">
                        <Refresh fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>No items found</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Add Item Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={700}>Add Inventory Item</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={8}><TextField fullWidth label="Item Name" size="small" value={form.name} onChange={e => setForm((f: any) => ({ ...f, name: e.target.value }))} /></Grid>
            <Grid item xs={12} sm={4}>
              <TextField select fullWidth label="Category" size="small" value={form.category} onChange={e => setForm((f: any) => ({ ...f, category: e.target.value }))}>
                {categories.map(c => <MenuItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Quantity" type="number" size="small" value={form.quantity} onChange={e => setForm((f: any) => ({ ...f, quantity: Number(e.target.value) }))} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Min Stock" type="number" size="small" value={form.minimumStock} onChange={e => setForm((f: any) => ({ ...f, minimumStock: Number(e.target.value) }))} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Unit" size="small" value={form.unit} onChange={e => setForm((f: any) => ({ ...f, unit: e.target.value }))} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Cost Price (₹)" type="number" size="small" value={form.costPrice} onChange={e => setForm((f: any) => ({ ...f, costPrice: Number(e.target.value) }))} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Selling Price (₹)" type="number" size="small" value={form.sellingPrice} onChange={e => setForm((f: any) => ({ ...f, sellingPrice: Number(e.target.value) }))} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Manufacturer" size="small" value={form.manufacturer} onChange={e => setForm((f: any) => ({ ...f, manufacturer: e.target.value }))} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Batch Number" size="small" value={form.batchNumber} onChange={e => setForm((f: any) => ({ ...f, batchNumber: e.target.value }))} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Expiry Date" type="date" size="small" value={form.expiryDate} onChange={e => setForm((f: any) => ({ ...f, expiryDate: e.target.value }))} InputLabelProps={{ shrink: true }} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd} sx={{ borderRadius: 2 }}>Add Item</Button>
        </DialogActions>
      </Dialog>

      {/* Restock Dialog */}
      <Dialog open={!!restockItem} onClose={() => setRestockItem(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={700}>Restock: {restockItem?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>Current stock: {restockItem?.quantity} {restockItem?.unit}</Typography>
          <TextField fullWidth label="Add Quantity" type="number" value={restockQty} onChange={e => setRestockQty(Number(e.target.value))} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setRestockItem(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleRestock} sx={{ borderRadius: 2 }}>Restock</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Inventory;
